import React, { useEffect, useState } from 'react';
import { Car } from 'lucide-react';
import type { AutomobileBodyType, AutomobileModel } from '../../types/automobile';
import { AUTOMOBILE_BODY_CHIP, AUTOMOBILE_BODY_TYPE_LABELS } from '../../types/automobile';
import {
  getAutomobileModelDisplayName,
  getAutomobileModelProductionYears,
} from '../../utils/automobileModelDisplay';
import AutomobileModelImage from './AutomobileModelImage';

interface Props {
  model: AutomobileModel;
  brandName: string;
  darkMode: boolean;
  hideBaseName?: boolean;
}

const AutomobileModelCard: React.FC<Props> = ({ model, brandName, darkMode, hideBaseName }) => {
  const [imgFailed, setImgFailed] = useState(false);

  const displayName = getAutomobileModelDisplayName(model);
  const years = getAutomobileModelProductionYears(model);
  const showTitle = !hideBaseName;
  const hasMeta = years.length > 0 || !!model.description;

  const card = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm';
  const heading = darkMode ? 'text-white' : 'text-gray-900';
  const text = darkMode ? 'text-gray-400' : 'text-gray-500';
  const imgBg = darkMode ? 'bg-gray-900/60' : 'bg-gray-100';
  const yearChip = darkMode
    ? 'border-gray-600 text-gray-200 bg-gray-900/40'
    : 'border-gray-200 text-gray-700 bg-white/70';

  useEffect(() => {
    setImgFailed(false);
  }, [model.imageUrl, model.imageFocalX, model.imageFocalY, model.imageZoom]);

  const imageFrame = {
    focalX: model.imageFocalX,
    focalY: model.imageFocalY,
    zoom: model.imageZoom,
  };

  return (
    <article className={`rounded-xl border overflow-hidden flex flex-col ${card}`}>
      <div className={`relative aspect-[16/10] overflow-hidden ${imgBg}`}>
        {!imgFailed && model.imageUrl ? (
          <AutomobileModelImage
            src={model.imageUrl}
            alt={`${brandName} ${displayName}`}
            frame={imageFrame}
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2">
            <Car size={40} className="text-indigo-400/70" aria-hidden />
            <span className={`text-xs ${text}`}>Görsel yüklenemedi</span>
          </div>
        )}
        <span
          className={`absolute top-2 right-2 px-2 py-0.5 rounded-md border text-xs font-medium backdrop-blur-sm ${
            AUTOMOBILE_BODY_CHIP[model.bodyType as AutomobileBodyType]
          }`}
        >
          {AUTOMOBILE_BODY_TYPE_LABELS[model.bodyType as AutomobileBodyType]}
        </span>
      </div>
      {hasMeta ? (
        <div className="p-3 sm:p-4 flex-1">
          {showTitle ? (
            <h3 className={`font-semibold text-sm sm:text-base leading-snug ${heading}`}>{displayName}</h3>
          ) : null}
          {years.length > 0 ? (
            <div className={`flex flex-wrap items-center gap-1.5 ${showTitle ? 'mt-2' : ''}`}>
              {years.map((y) => (
                <span
                  key={y}
                  className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium tabular-nums ${yearChip}`}
                  title="Üretim yılı"
                >
                  {y}
                </span>
              ))}
            </div>
          ) : null}
          {model.description ? (
            <p className={`text-xs mt-2 leading-relaxed line-clamp-4 ${text}`}>{model.description}</p>
          ) : null}
        </div>
      ) : null}
    </article>
  );
};

export default AutomobileModelCard;
